import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Boxes, Info, Scale, CheckCircle2, XCircle, Power, PowerOff } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER_CEO";
  return (
    <div>
      <PageHeader title="Pengaturan" subtitle="Profil perusahaan & informasi sistem." testId="settings-header" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2"><Building2 className="h-5 w-5 text-indigo-600" /><h3 className="font-display text-lg font-semibold text-slate-900">Profil Perusahaan</h3></div>
          <dl className="space-y-3 text-sm">
            <Row label="Nama" value="SPH ASCENDRA" />
            <Row label="Divisi" value="SPH Factory · SPH Digital Creative · SPH Online Commerce" />
            <Row label="Mata Uang" value="Indonesian Rupiah (Rp)" />
            <Row label="Fase Sistem" value="Phase 1 — Online Shop & Inventory" />
          </dl>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2"><Info className="h-5 w-5 text-indigo-600" /><h3 className="font-display text-lg font-semibold text-slate-900">Akun Anda</h3></div>
          <dl className="space-y-3 text-sm">
            <Row label="Nama" value={user?.name} />
            <Row label="Email" value={user?.email} />
            <Row label="Role" value={user?.role_name} />
          </dl>
        </div>

        {isOwner && <CatchWeightFlagControl />}

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
          <div className="mb-4 flex items-center gap-2"><Boxes className="h-5 w-5 text-indigo-600" /><h3 className="font-display text-lg font-semibold text-slate-900">Arsitektur Inventory</h3></div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Barang jadi disimpan di <strong>satu Inventory Pusat</strong> sebagai stok bersama global untuk seluruh 10 toko online.
            SKU berformat <span className="font-mono text-indigo-700">Bahan-Umur-Lengan-Warna-Ukuran</span> dan tidak memuat informasi toko maupun desain.
            Inventory Konveksi dan Inventory Digital Printing dikelola terpisah dengan saldo yang tidak digabung.
          </p>
        </div>
      </div>
    </div>
  );
}

function CatchWeightFlagControl() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [reason, setReason] = useState("");

  const load = () => api.get("/settings/catch-weight/flag").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const enable = async () => {
    setBusy(true);
    try { await api.put("/settings/catch-weight/flag", { enabled: true, reason: reason || null }); toast.success("Catch Weight DIAKTIFKAN"); setReason(""); await load(); }
    catch (e) { toast.error(apiError(e)); await load(); } finally { setBusy(false); }
  };
  const disable = async (confirm) => {
    setBusy(true);
    try { await api.put("/settings/catch-weight/flag", { enabled: false, reason: reason || null, confirm }); toast.success("Catch Weight DINONAKTIFKAN"); setReason(""); setConfirmOff(false); await load(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d) { setConfirmOff(true); toast.warning(typeof d === "string" ? d : d.message); }
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  if (!data) return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2" data-testid="cw-flag-loading">
      <div className="mb-2 flex items-center gap-2"><Scale className="h-5 w-5 text-indigo-600" /><h3 className="font-display text-lg font-semibold text-slate-900">Fitur Fabric Catch Weight</h3></div>
      <p className="text-sm text-slate-400">Memuat…</p>
    </div>
  );

  const on = data.enabled;
  const act = data.active_cw_transactions || {};
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2" data-testid="cw-flag-control">
      <div className="mb-1 flex items-center gap-2">
        <Scale className="h-5 w-5 text-indigo-600" />
        <h3 className="font-display text-lg font-semibold text-slate-900">Fitur Fabric Catch Weight</h3>
        <Badge className={on ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"} data-testid="cw-flag-status">{on ? "ON" : "OFF"}</Badge>
      </div>
      <p className="mb-4 text-sm text-slate-600" data-testid="cw-flag-explainer">
        {on
          ? "Catch Weight ON — hanya item dengan tracking_mode=CATCH_WEIGHT_ROLL yang menggunakan alur Catch Weight. Item legacy/NORMAL tetap memakai alur lama."
          : "Catch Weight OFF — operasi Catch Weight belum dapat digunakan."}
      </p>

      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="mb-2 text-sm font-semibold text-slate-700">Safety check pra-aktivasi</div>
        <div className="grid gap-1.5 sm:grid-cols-2" data-testid="cw-flag-checks">
          {(data.safety_checks || []).map((c) => (
            <div key={c.code} className="flex items-start gap-1.5 text-xs">
              {c.pass ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />}
              <span><span className="font-medium">{c.code}</span> — {c.detail}</span>
            </div>
          ))}
        </div>
        {data.pilot_items?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 border-t border-slate-200 pt-2 text-xs" data-testid="cw-flag-pilot">
            {data.pilot_items.map((p) => (
              <span key={p.inventory_item_id} className={p.healthy ? "text-emerald-700" : "text-rose-600 font-medium"}>
                {p.healthy ? "✓" : "✗"} {p.item_name} ({p.custody_roll_count}/{p.on_hand})
              </span>
            ))}
            <span className="w-full text-slate-400">on_hand=0 &amp; 0 roll = sehat (item kain baru belum GR). Jika on_hand&gt;0 wajib custody==on_hand.</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1">
          <Label className="text-xs">Catatan (opsional)</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="alasan perubahan" className="w-64" data-testid="cw-flag-reason" />
        </div>
        {!on
          ? <Button onClick={enable} disabled={busy || !data.can_enable} data-testid="cw-flag-enable-btn" className="bg-emerald-600 hover:bg-emerald-500"><Power className="mr-1.5 h-4 w-4" />Aktifkan Catch Weight</Button>
          : <Button onClick={() => disable(false)} disabled={busy} variant="outline" className="text-rose-600 border-rose-300" data-testid="cw-flag-disable-btn"><PowerOff className="mr-1.5 h-4 w-4" />Nonaktifkan Catch Weight</Button>}
        {!on && !data.can_enable && <span className="text-xs text-rose-600" data-testid="cw-flag-blocked-note">Aktivasi diblokir — safety check belum lolos.</span>}
      </div>

      <Dialog open={confirmOff} onOpenChange={setConfirmOff}>
        <DialogContent data-testid="cw-flag-confirm-off">
          <DialogHeader><DialogTitle>Nonaktifkan Catch Weight?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Ada transaksi Catch Weight aktif/pending (FRI: {act.active_fri || 0}, issue ops: {act.pending_issue_ops || 0}, migrasi ops: {act.pending_migration_ops || 0}).
            Menonaktifkan tidak mengubah/menghapus roll registry, tidak rollback migrasi, dan tidak menghapus history — hanya mencegah operasi CW baru. Lanjutkan?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOff(false)}>Batal</Button>
            <Button className="bg-rose-600 hover:bg-rose-500" onClick={() => disable(true)} disabled={busy} data-testid="cw-flag-confirm-off-btn">Ya, Nonaktifkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4"><dt className="text-slate-400">{label}</dt><dd className="text-right font-medium text-slate-800">{value}</dd></div>
);
